import type {
  RMA,
  ReturnShipment,
  ReturnReceipt,
  RefundRecord,
  WarrantyClaim,
} from './types';

export function adaptRMA(raw: any): RMA {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'rma-fallback',
      rma_number: 'RMA-UNKNOWN',
      reason: 'Unknown',
      resolution: 'RESTOCK',
      status: 'PENDING',
    };
  }
  return {
    id: String(raw.id || 'rma-fallback'),
    rma_number: String(raw.rma_number || 'RMA-UNKNOWN'),
    customer_id: raw.customer_id ? String(raw.customer_id) : undefined,
    customer_name: raw.customer_name ? String(raw.customer_name) : undefined,
    so_id: raw.so_id ? String(raw.so_id) : undefined,
    reason: String(raw.reason || 'General Return'),
    resolution: String(raw.resolution || 'RESTOCK'),
    status: String(raw.status || 'PENDING'),
    created_at: raw.created_at ? String(raw.created_at) : undefined,
    lines: Array.isArray(raw.lines)
      ? raw.lines.map((l: any) => ({
          sku_id: String(l.sku_id || ''),
          quantity: Number(l.quantity) || 1,
          condition: String(l.condition || 'OPEN_BOX'),
        }))
      : [],
  };
}

export function adaptReturnShipment(raw: any): ReturnShipment {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'ship-fallback',
      rma_number: 'RMA-UNKNOWN',
      tracking_number: 'TRK-000',
      carrier: 'Carrier',
      origin_city: 'Unknown',
      status: 'IN_TRANSIT',
    };
  }
  return {
    id: String(raw.id || 'ship-fallback'),
    rma_number: String(raw.rma_number || 'RMA-UNKNOWN'),
    tracking_number: String(raw.tracking_number || 'TRK-000'),
    carrier: String(raw.carrier || 'Freight Line'),
    origin_city: String(raw.origin_city || 'Origin Port'),
    status: (raw.status as any) || 'IN_TRANSIT',
  };
}

export function adaptReturnReceipt(raw: any): ReturnReceipt {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'receipt-fallback',
      rma_id: 'rma-fallback',
      received_at: new Date().toISOString(),
      received_by: 'Inspector',
      quantity: 1,
      disposition: 'RESTOCK',
      notes: '',
    };
  }
  return {
    id: String(raw.id || 'receipt-fallback'),
    rma_id: String(raw.rma_id || 'rma-fallback'),
    received_at: String(raw.received_at || new Date().toISOString()),
    received_by: String(raw.received_by || 'Inspector'),
    quantity: Number(raw.quantity) || 1,
    disposition: String(raw.disposition || 'RESTOCK'),
    notes: String(raw.notes || ''),
    warehouse_id: raw.warehouse_id ? String(raw.warehouse_id) : undefined,
  };
}

export function adaptRefund(raw: any): RefundRecord {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'ref-fallback',
      rma_id: 'rma-fallback',
      customer_name: 'Customer',
      amount_minor: 0,
      currency: 'USD',
      status: 'PENDING',
    };
  }
  return {
    id: String(raw.id || 'ref-fallback'),
    rma_id: String(raw.rma_id || 'rma-fallback'),
    customer_name: String(raw.customer_name || 'Customer'),
    amount_minor: Number(raw.amount_minor) || 0,
    currency: String(raw.currency || 'USD'),
    status: String(raw.status || 'PENDING'),
    processed_at: raw.processed_at ? String(raw.processed_at) : undefined,
  };
}

export function adaptWarrantyClaim(raw: any): WarrantyClaim {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'claim-fallback',
      claim_number: 'CLM-UNKNOWN',
      equipment_serial: 'SN-000',
      customer_name: 'Customer',
      warranty_expires_at: new Date().toISOString(),
      issue_description: '',
      status: 'SUBMITTED',
    };
  }
  return {
    id: String(raw.id || 'claim-fallback'),
    claim_number: String(raw.claim_number || 'CLM-UNKNOWN'),
    equipment_serial: String(raw.equipment_serial || 'SN-000'),
    sku_id: raw.sku_id ? String(raw.sku_id) : undefined,
    customer_name: String(raw.customer_name || 'Customer'),
    warranty_expires_at: String(raw.warranty_expires_at || new Date().toISOString()),
    issue_description: String(raw.issue_description || ''),
    status: String(raw.status || 'SUBMITTED'),
  };
}

export const sampleRMAs: RMA[] = [
  { id: '1', rma_number: 'RMA-2026-0001', reason: 'Defective bearing', resolution: 'RESTOCK', status: 'APPROVED' },
  { id: '2', rma_number: 'RMA-2026-0002', reason: 'Incorrect model shipped', resolution: 'RESTOCK', status: 'RECEIVED' },
  { id: '3', rma_number: 'RMA-2026-0003', reason: 'Transit impact damage', resolution: 'SCRAP', status: 'INSPECTED' },
];

export const sampleReturnShipments: ReturnShipment[] = [
  { id: '1', rma_number: 'RMA-2026-0001', tracking_number: '1Z9999999999999999', carrier: 'UPS Ground', origin_city: 'Austin, TX', status: 'IN_TRANSIT' },
  { id: '2', rma_number: 'RMA-2026-0002', tracking_number: '9400100000000000000000', carrier: 'USPS Priority', origin_city: 'Seattle, WA', status: 'DELIVERED' },
  { id: '3', rma_number: 'RMA-2026-0003', tracking_number: '794830194820', carrier: 'FedEx Express', origin_city: 'Boston, MA', status: 'IN_TRANSIT' },
];

export const sampleReturnReceipts: ReturnReceipt[] = [
  { id: '1', rma_id: '1', received_at: new Date().toISOString(), received_by: 'Alex Dockmaster', quantity: 2, disposition: 'RESTOCK', notes: 'Factory sealed' },
  { id: '2', rma_id: '2', received_at: new Date().toISOString(), received_by: 'Sara QA', quantity: 1, disposition: 'SCRAP', notes: 'Severe casing fracture' },
];

export const sampleRefunds: RefundRecord[] = [
  { id: '1', rma_id: '1', customer_name: 'Apex Industrial Systems', amount_minor: 125000, currency: 'USD', status: 'PENDING' },
  { id: '2', rma_id: '2', customer_name: 'Global Metals Corp', amount_minor: 48000, currency: 'USD', status: 'PROCESSED' },
];

export const sampleWarrantyClaims: WarrantyClaim[] = [
  { id: '1', claim_number: 'CLM-8412', equipment_serial: 'SN-TITAN-98412', customer_name: 'Apex Industrial Systems', warranty_expires_at: new Date().toISOString(), issue_description: 'Sensor voltage fluctuating', status: 'APPROVED' },
  { id: '2', claim_number: 'CLM-8413', equipment_serial: 'SN-VALVE-1102', customer_name: 'Precision Dynamics', warranty_expires_at: new Date().toISOString(), issue_description: 'Seal leak under high pressure', status: 'SUBMITTED' },
];
